import React, { useEffect, useState } from 'react';
import { useCategoriesStore } from '../../../../store/categoriesStore';
import { Category } from '../../../../services/categoriesApi';
import styles from './AddCategoryModal.module.css';

interface EditCategoryModalProps {
  open: boolean;
  category: Category | null;
  onClose: () => void;
}

export const EditCategoryModal: React.FC<EditCategoryModalProps> = ({ open, category, onClose }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [parentId, setParentId] = useState<string>('');
  const [isActive, setIsActive] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const categories = useCategoriesStore((s) => s.items);
  const update = useCategoriesStore((s) => s.update);

  useEffect(() => {
    if (open && category) {
      setName(category.name);
      setDescription(category.description || '');
      setParentId(category.parent_id ? String(category.parent_id) : '');
      setIsActive(category.is_active);
      setError(null);
      setSubmitting(false);
    }
  }, [open, category]);

  if (!open || !category) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { setError('Name is required'); return; }
    setError(null);
    setSubmitting(true);
    try {
      await update(category.id, {
        name: name.trim(),
        description: description.trim() || undefined,
        parent_id: parentId ? Number(parentId) : null,
        is_active: isActive,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update category');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.overlay} onClick={onClose} role="dialog" aria-modal="true">
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <header className={styles.header}>
          <h2 className={styles.title}>Edit Category</h2>
          <button className={styles.close} onClick={onClose} aria-label="Close">×</button>
        </header>

        <form onSubmit={handleSubmit} className={styles.form}>
          {error && <p className={styles.error}>{error}</p>}

          <label className={styles.field}>
            <span>Name <span className={styles.required}>*</span></span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoFocus
              maxLength={120}
            />
          </label>

          <label className={styles.field}>
            <span>Description</span>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              maxLength={500}
            />
          </label>

          <label className={styles.field}>
            <span>Parent Category</span>
            <select value={parentId} onChange={(e) => setParentId(e.target.value)}>
              <option value="">— None (top-level) —</option>
              {categories.filter((c) => c.id !== category.id).map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </label>

          <label className={styles.toggle}>
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
            <span>Active</span>
          </label>

          <div className={styles.actions}>
            <button type="button" className={styles.cancel} onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className={styles.submit} disabled={submitting}>
              {submitting ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditCategoryModal;
